/*
 * Sticky card stack.
 *
 * Every card is `position: sticky`, so a card pins near the top of the viewport
 * and the next one scrolls up over it. This script only adds the second half of
 * the effect: as a card gets covered, it shrinks a little so the stack reads as
 * a physical pile rather than a flat overlap.
 *
 * It also swaps the copy beside the stack, so the panel on the left always
 * belongs to the card currently at the front.
 *
 * Each card gets `--nb-d`: how many cards are currently lying on top of it, as a
 * fraction. The CSS turns that into a scale and a dim, so a card two layers back
 * is visibly narrower and darker than one layer back, which is what sells the
 * depth. Without JS the stacking still works, cards just do not recede.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initStack(stack) {
    var items = Array.prototype.slice.call(
      stack.querySelectorAll('[data-nb-stack-item]')
    );
    if (items.length < 2) return;

    var queued = false;
    var grid = stack.closest('.nb-showcase__grid');
    var copy = grid ? grid.querySelector('.nb-showcase__sticky') : null;

    // The copy column and the card column are grid siblings, so they share a
    // bottom edge. A sticky element lets go once that edge minus its own height
    // rises past its top offset, so the short copy block would otherwise hold on
    // long after the cards had left. Matching its height to where the last card
    // ends makes the two let go at the same moment and travel up together.

    var panels = grid ? Array.prototype.slice.call(grid.querySelectorAll('[data-nb-panel]')) : [];
    var activeIndex = -1;

    function setActive(index) {
      if (index === activeIndex || !panels.length) return;
      activeIndex = index;
      for (var i = 0; i < panels.length; i++) {
        panels[i].classList.toggle('is-active', i === index);
      }
    }

    function syncCopyHeight() {
      if (!copy) return;

      var panels = copy.querySelector('[data-nb-panels]');

      if (window.getComputedStyle(copy).position !== 'sticky') {
        // Narrow screens dissolve these boxes, so any height written here
        // would be measured against a layout that no longer exists.
        copy.style.removeProperty('min-height');
        if (panels) panels.style.removeProperty('height');
        return;
      }

      var last = items[items.length - 1];
      var copyTop = parseFloat(window.getComputedStyle(copy).top) || 0;
      var lastTop = parseFloat(window.getComputedStyle(last).top) || 0;

      copy.style.minHeight = last.offsetHeight + (lastTop - copyTop) + 'px';

      // Give the panels the height of a card so they can sit level with its
      // middle rather than starting at its top edge.
      if (panels) panels.style.height = items[0].offsetHeight + 'px';
    }

    function measure() {
      queued = false;

      // Step one: how far each card has been covered by the one after it, 0 to 1.
      var covered = [];

      for (var i = 0; i < items.length - 1; i++) {
        var current = items[i].getBoundingClientRect();
        var next = items[i + 1].getBoundingClientRect();
        var progress = 0;

        if (current.height > 0) {
          var overlap = current.top + current.height - next.top;
          progress = Math.min(Math.max(overlap / current.height, 0), 1);
        }

        covered.push(progress);
      }

      // Step two: a card's depth is every coverage from it onwards added up, so a
      // card with two cards fully stacked on it reads as depth 2, not depth 1.
      // Uncapped, so a card keeps receding for every further layer put on it.
      for (var j = 0; j < items.length; j++) {
        var depth = 0;

        for (var k = j; k < covered.length; k++) {
          depth += covered[k];
        }

        items[j].style.setProperty('--nb-d', depth.toFixed(4));

        // The first card's depth is the whole pile's, which is how far through
        // the stack we are, so rounding it names the card now at the front.
        if (j === 0) {
          setActive(Math.min(items.length - 1, Math.max(0, Math.round(depth))));
        }
      }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    }

    // Layout only, kept off the scroll path so it never thrashes a frame.
    function relayout() {
      syncCopyHeight();
      schedule();
    }

    function enable() {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', relayout, { passive: true });
      relayout();
    }

    function disable() {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', relayout);
      items.forEach(function (item) {
        item.style.removeProperty('--nb-d');
      });
      if (copy) copy.style.removeProperty('min-height');
    }

    function sync() {
      if (reduceMotion.matches) {
        disable();
      } else {
        enable();
      }
    }

    sync();

    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', sync);
    }

    // Cards can change height after images load.
    stack.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', relayout, { once: true });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-nb-stack]').forEach(initStack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-nb-stack]').forEach(initStack);
  });
})();
